/**
 * AnalysisContent + primitives
 *
 * The presentational core of the AI analysis, factored out of AnalysisPanel so
 * two surfaces render byte-for-byte identical markup:
 *   1. AnalysisPanel (in-app, authenticated) — fetches the job and spreads its
 *      report_* fields into AnalysisContent.
 *   2. SharedAnalysis (public, no-login) — renders a stored, sanitized snapshot.
 *
 * AnalysisContent renders only the body blocks (takeaway, metric cards, returns
 * matrix, common-size sections, narrative). The recommended-action badge lives
 * in each surface's header via <ActionBadge>. Every value here is a preformatted
 * display string; the narrative is the only HTML, and it is sanitized HERE, on
 * every surface: the model writes it from the sender's email and documents, and
 * a stored row may never have been sanitized at all.
 */

import { useMemo } from 'react';
import { ExternalLink, Info, MapPin, TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { StarRating } from '@/components/ui/StarRating';
import { cn } from '@/lib/utils';
import { PropertyMapThumbnail } from './shared/PropertyMapThumbnail';
import { sanitizeNarrativeHtml } from '../../../supabase/functions/_shared/sanitize-html';
import type {
  AnalysisAssumptions,
  AnalysisCommonSizeSection,
  AnalysisLeaseUp,
  AnalysisMetricCard,
  AnalysisPropertyCard,
  AnalysisReturnsMatrix,
  AnalysisSalesComp,
  AnalysisThreadOtherListing,
  CommonSizeSkipReason,
} from '@/types/ai-import';

export type RecommendedAction = 'pursue' | 'pass' | 'needs_info';

export const ACTION_BADGE: Record<
  RecommendedAction,
  { label: string; variant: 'green' | 'red' | 'yellow' }
> = {
  pursue: { label: 'Pursue', variant: 'green' },
  pass: { label: 'Pass', variant: 'red' },
  needs_info: { label: 'Needs info', variant: 'yellow' },
};

export function ActionBadge({ action }: { action?: RecommendedAction | null }) {
  if (!action) return null;
  const cfg = ACTION_BADGE[action];
  if (!cfg) return null;
  return (
    <Badge size="sm" variant={cfg.variant}>
      {cfg.label}
    </Badge>
  );
}

export function FlagChip({ flag }: { flag: 'opportunity' | 'issue' | null }) {
  if (!flag) return null;
  return flag === 'opportunity' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
      <TrendingUp className="h-3 w-3" /> Opportunity
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:bg-red-900/30 dark:text-red-300">
      <TrendingDown className="h-3 w-3" /> Issue
    </span>
  );
}

export function ReturnsMatrixTable({
  matrix,
  title,
}: {
  matrix: AnalysisReturnsMatrix;
  title: string;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</div>
      <div className="text-xs text-gray-400 mb-1.5 dark:text-gray-500">{matrix.subtitle}</div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <th className="px-3 py-2 font-medium">Price</th>
              <th className="px-3 py-2 font-medium">$/Pad</th>
              <th className="px-3 py-2 font-medium">Cap rate</th>
              <th className="px-3 py-2 font-medium">IRR</th>
              <th className="px-3 py-2 font-medium">MOIC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {matrix.rows.map((r, i) => (
              <tr key={i} className={r.isCenter ? 'bg-primary-50/60 font-medium dark:bg-primary-900/20' : undefined}>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-900 dark:text-gray-100">
                  {r.price}
                  {r.isCenter && <span className="ml-1.5 text-xs text-primary-600 dark:text-primary-400">ASK</span>}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.pricePerPad}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.capRate}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.irr}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.moic}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MetricCards({ metrics }: { metrics: AnalysisMetricCard[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {metrics.map((m, i) => (
        <div key={i} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{m.label}</div>
          <div className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-gray-100">{m.value}</div>
          {m.caption && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{m.caption}</div>}
        </div>
      ))}
    </div>
  );
}

export function CommonSizeSection({ section }: { section: AnalysisCommonSizeSection }) {
  // Ours-only sections have no seller data, so the theirs/Δ/as-presented columns
  // are all blank — collapse them instead of showing rows of "—".
  const oursOnly = !!section.oursOnly;
  // Flattened to a heading + tables (no outer border card): this section already
  // renders inside the AnalysisPanel frame, and the gray comparison block below
  // is the single inset — avoids the modal > card > card > card nesting.
  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.title}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{section.subtitle}</div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1.5 pr-3 font-medium">Line item</th>
              {!oursOnly && <th className="py-1.5 pr-3 font-medium">Theirs</th>}
              <th className="py-1.5 pr-3 font-medium">{oursOnly ? 'Our norm /lot' : 'Ours'}</th>
              {!oursOnly && <th className="py-1.5 pr-3 font-medium">Δ total/yr</th>}
              <th className="py-1.5 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {section.rows.map((r, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-3 text-gray-900 dark:text-gray-100">{r.label}</td>
                {!oursOnly && (
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.theirs}</td>
                )}
                <td className="py-1.5 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.ours}</td>
                {!oursOnly && (
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{r.delta}</td>
                )}
                <td className="py-1.5">
                  <FlagChip flag={r.flag} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {section.flagNotes.length > 0 && (
        <ul className="space-y-1">
          {section.flagNotes.map((n, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
              {n}
            </li>
          ))}
        </ul>
      )}

      <div className="overflow-x-auto rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {section.comparison.title}
        </div>
        <div className="text-xs text-gray-400 mb-1.5 dark:text-gray-500">{section.comparison.subtitle}</div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1 pr-3 font-medium" />
              {!oursOnly && <th className="py-1 pr-3 font-medium">As presented</th>}
              <th className="py-1 font-medium">As we&rsquo;d run it</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200/70 dark:divide-gray-800">
            {section.comparison.rows.map((r, i) => (
              <tr key={i}>
                <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">{r.label}</td>
                {!oursOnly && (
                  <td className="py-1 pr-3 whitespace-nowrap text-gray-900 dark:text-gray-100">{r.asIs}</td>
                )}
                <td className="py-1 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">{r.flow}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {section.flowMatrix && (
        <ReturnsMatrixTable matrix={section.flowMatrix} title="Returns at our underwriting" />
      )}
    </div>
  );
}

export function LeaseUpSection({ section }: { section: AnalysisLeaseUp }) {
  type Row = AnalysisLeaseUp['rows'][number];
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wide text-gray-900 border-b-2 border-gray-200 pb-1 mb-2 dark:text-gray-100 dark:border-gray-700">
        {section.propertyName ? `${section.propertyName} — ` : ''}Lease-up upside — stabilized snapshot
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-gray-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-gray-100">
        <Badge className="mr-1 bg-amber-600 text-white dark:bg-amber-600 dark:text-white">ISSUE</Badge>
        {section.issueNote}
      </div>
      {section.rows.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr className="text-gray-400 dark:text-gray-500">
                <th className="border-b border-gray-200 px-2 py-1.5 text-left dark:border-gray-700"></th>
                {section.rows.map((r, i) => (
                  <th
                    key={i}
                    className={cn(
                      'border-b border-gray-200 px-2 py-1.5 text-right dark:border-gray-700',
                      r.isTarget && 'bg-blue-50 font-bold dark:bg-blue-950/40',
                    )}
                  >
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['Occupied lots', (r: Row) => r.occupiedLots],
                  ['Lots to fill', (r: Row) => r.lotsToFill],
                  ['NOI (our opex)', (r: Row) => r.noi],
                  ['Cap on ask', (r: Row) => r.cap],
                ] as const
              ).map(([label, pick]) => (
                <tr key={label}>
                  <td className="border-b border-gray-200 px-2 py-1.5 text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    {label}
                  </td>
                  {section.rows.map((r, i) => (
                    <td
                      key={i}
                      className={cn(
                        'border-b border-gray-200 px-2 py-1.5 text-right text-gray-900 dark:border-gray-700 dark:text-gray-100',
                        r.isTarget && 'bg-blue-50 font-bold dark:bg-blue-950/40',
                      )}
                    >
                      {pick(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {section.caption && (
        <div className="mt-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">{section.caption}</div>
      )}
    </div>
  );
}

/**
 * The per-property block the report email has always had: each park with its
 * own address, appearance rating, Flow link and full metric set. Without it the
 * in-app analysis showed only blended portfolio numbers, and a multi-park
 * package read as though it were one asset.
 */
export function PropertyAnalysisCards({
  properties,
  grouping,
}: {
  properties: AnalysisPropertyCard[];
  grouping?: 'single_offering' | 'separate_listings' | null;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {grouping === 'separate_listings' ? 'Each property — individual analysis' : 'Properties'}
      </div>
      {properties.map((p, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <PropertyMapThumbnailFromAddress address={p.address} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {p.propertyId ? (
                  <Link
                    to={`/properties/${p.propertyId}`}
                    className="font-semibold text-primary-700 hover:underline dark:text-primary-400"
                  >
                    {p.name}
                  </Link>
                ) : (
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{p.name}</span>
                )}
                {p.star && (
                  <StarRating
                    value={p.star.value}
                    low={p.star.low}
                    high={p.star.high}
                    size="sm"
                    showValue
                    showTooltip
                  />
                )}
              </div>
              {p.address && p.address !== '—' && (
                <a
                  href={p.mapsLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={cn(
                    'mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400',
                    p.mapsLink && 'hover:text-gray-700 hover:underline dark:hover:text-gray-200',
                  )}
                >
                  <MapPin className="h-3 w-3 shrink-0" />
                  {p.address}
                </a>
              )}
              {/* Flex-wrap, not a grid: these metrics range from "70%" to
                  "$14,500,000", and equal grid tracks make the wide ones
                  overrun the next column. */}
              {p.metrics.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                  {p.metrics.map((m, j) => (
                    <div key={j} className="min-w-[4.5rem]">
                      <div className="whitespace-nowrap text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {m.label}
                      </div>
                      <div className="whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {p.propertyId && (
                <Link
                  to={`/properties/${p.propertyId}`}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400"
                >
                  Full analysis in Flow <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The card carries one joined address string; split it back for geocoding. */
function PropertyMapThumbnailFromAddress({ address }: { address: string }) {
  const parts = address === '—' ? [] : address.split(',').map((s) => s.trim());
  const [street, city, state, zip] = parts;
  return (
    <PropertyMapThumbnail
      street={street}
      city={city}
      state={state}
      zip={zip}
      className="h-16 w-24"
    />
  );
}

export function SalesCompsTable({ comps }: { comps: AnalysisSalesComp[] }) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">Sales comps</div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <th className="px-3 py-2 font-medium">Property</th>
              <th className="px-3 py-2 font-medium">Price</th>
              <th className="px-3 py-2 font-medium">Cap rate</th>
              <th className="px-3 py-2 font-medium">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {comps.map((c, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{c.property}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.price}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.capRate}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{c.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Listings an older message of a forwarded thread pitched — kept out of every
 * figure above, and named so the reader knows Flow saw them. Mirrors the
 * report email's box.
 */
export function ThreadOtherListingsNote({ listings }: { listings: AnalysisThreadOtherListing[] }) {
  const one = listings.length === 1;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Also pitched earlier in this thread — not part of this analysis
      </div>
      <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-gray-900 dark:text-gray-100">
        {listings.map((o, i) => (
          <li key={i}>
            <span className="font-medium">{o.name}</span>
            {o.detail && <span className="text-gray-500 dark:text-gray-400"> — {o.detail}</span>}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        An older message in the forwarded thread pitched {one ? 'this listing' : 'these listings'}, so{' '}
        {one ? 'it is' : 'they are'} left out of the figures above. Forward that message on its own to have Flow
        analyze {one ? 'it' : 'them'}.
      </p>
    </div>
  );
}

const SKIP_MESSAGES: Record<CommonSizeSkipReason, string> = {
  no_properties: 'No property could be identified in this package, so no common-sized comparison was run.',
  no_lots: 'No lot/pad count was found, so a per-lot common-sized comparison couldn’t be run — add the pad count to benchmark this deal.',
  no_template: 'No portfolio common-size template matches this park’s size yet.',
  error: 'The common-sized comparison could not be completed. Re-run the analysis to try again.',
};

/** Shown in place of the comparison when it couldn't run — never a silent blank. */
export function CommonSizeSkipNote({ reason }: { reason: CommonSizeSkipReason }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
      <span>
        <span className="font-semibold text-gray-900 dark:text-gray-100">Common-size comparison not run.</span>{' '}
        {SKIP_MESSAGES[reason]}
      </span>
    </div>
  );
}

/** Disclosed assumptions + methodology behind every figure — always rendered. */
export function AssumptionsSection({ assumptions }: { assumptions: AnalysisAssumptions }) {
  const pct = (v: number, d = 0) => `${(v * 100).toFixed(d)}%`;
  const rows: Array<[string, string]> = [
    ['Rent growth', `${pct(assumptions.revenue_growth)}/yr`],
    ['Expense growth', `${pct(assumptions.expense_growth)}/yr`],
    ['Exit cap', assumptions.exit_cap_basis],
    ['Financing', `${pct(assumptions.ltv)} LTV · ${pct(assumptions.rate, 1)} rate · ${assumptions.amort_years}-yr amort`],
    ['Hold period', `${assumptions.hold_years} years`],
    [
      'Costs',
      `${pct(assumptions.selling_pct)} selling · ${pct(assumptions.closing_pct)} closing · ${pct(assumptions.mgmt_fee_pct)} mgmt`,
    ],
  ];
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        Assumptions &amp; methodology
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{assumptions.scenario_label}</span>
      </div>
      <div className="overflow-x-auto rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-gray-200/70 dark:divide-gray-700/70">
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td className="py-1 pr-3 whitespace-nowrap text-gray-500 dark:text-gray-400">{k}</td>
                <td className="py-1 font-medium text-gray-900 dark:text-gray-100">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul className="mt-2 space-y-1">
          {assumptions.methodology.map((m, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
              {m}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export interface AnalysisContentProps {
  oneLineTakeaway?: string | null;
  metrics?: AnalysisMetricCard[] | null;
  returnsMatrix?: AnalysisReturnsMatrix | null;
  commonSize?: AnalysisCommonSizeSection[] | null;
  /** Why the comparison didn't run, shown only when commonSize is empty. */
  commonSizeSkipReason?: CommonSizeSkipReason | null;
  leaseUp?: AnalysisLeaseUp[] | null;
  /** Disclosed assumptions + methodology, rendered whenever present. */
  assumptions?: AnalysisAssumptions | null;
  /** Per-property cards (email parity). Omitted on the public share page. */
  properties?: AnalysisPropertyCard[] | null;
  /** Sale comps (email parity). Omitted on the public share page. */
  salesComps?: AnalysisSalesComp[] | null;
  /** Listings set aside from a forwarded thread (email parity). Omitted on the public share page. */
  threadOtherListings?: AnalysisThreadOtherListing[] | null;
  /**
   * Labels the blended block as a portfolio when the package is a rundown of
   * separate listings, so those numbers can't read as one park's.
   */
  grouping?: 'single_offering' | 'separate_listings' | null;
  /** Model-written HTML, as stored. Sanitized here before it renders. */
  narrativeHtml?: string | null;
  className?: string;
}

/** The analysis body blocks, shared by the in-app panel and the public page. */
export function AnalysisContent({
  oneLineTakeaway,
  metrics,
  returnsMatrix,
  commonSize,
  commonSizeSkipReason,
  leaseUp,
  assumptions,
  properties,
  salesComps,
  threadOtherListings,
  grouping,
  narrativeHtml,
  className,
}: AnalysisContentProps) {
  const hasCommonSize = !!commonSize && commonSize.length > 0;
  const propertyCount = properties?.length ?? 0;
  const safeNarrativeHtml = useMemo(() => sanitizeNarrativeHtml(narrativeHtml), [narrativeHtml]);
  return (
    <div className={cn('space-y-4', className)}>
      {oneLineTakeaway && (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{oneLineTakeaway}</p>
      )}

      {/* Name the blended block, as the email does: without this, portfolio
          totals across several parks read as one park's numbers. */}
      {propertyCount > 1 && metrics && metrics.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {grouping === 'separate_listings' ? `Portfolio — all ${propertyCount} together` : 'Portfolio'}
          </div>
          {grouping === 'separate_listings' && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Blended totals as if the {propertyCount} parks were bought as one deal.
            </div>
          )}
        </div>
      )}

      {metrics && metrics.length > 0 && <MetricCards metrics={metrics} />}

      {returnsMatrix && <ReturnsMatrixTable matrix={returnsMatrix} title="Returns sensitivity" />}

      {commonSize?.map((section, i) => <CommonSizeSection key={i} section={section} />)}

      {!hasCommonSize && commonSizeSkipReason && <CommonSizeSkipNote reason={commonSizeSkipReason} />}

      {leaseUp?.map((section, i) => <LeaseUpSection key={i} section={section} />)}

      {assumptions && <AssumptionsSection assumptions={assumptions} />}

      {/* Same order as the report email: portfolio block, then each property,
          then the narrative, then comps. */}
      {propertyCount > 0 && (
        <PropertyAnalysisCards properties={properties!} grouping={grouping} />
      )}

      {threadOtherListings && threadOtherListings.length > 0 && (
        <ThreadOtherListingsNote listings={threadOtherListings} />
      )}

      {safeNarrativeHtml && (
        <div
          className="prose prose-sm max-w-prose text-sm text-gray-700 dark:text-gray-300"
          dangerouslySetInnerHTML={{ __html: safeNarrativeHtml }}
        />
      )}

      {salesComps && salesComps.length > 0 && <SalesCompsTable comps={salesComps} />}
    </div>
  );
}
